import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Language,
  useCompaniesQuery,
  useCreateCompanyLiteMutation,
  useCreateCompanyUserTokenByCompanyIdMutation,
} from "../../graphql/generated/graphql";
import { getToken, setToken } from "../../graphql/token";
import { safeParseInt } from "../../utils";

export default function CompanyManagement() {
  const [searchParams] = useSearchParams();
  const [error, setError] = useState<string | null>(null);

  const phone = searchParams.get("phone") || "";
  const token = getToken();

  if (!phone || !token) {
    window.location.href = "/logout";
  }

  const { data, loading } = useCompaniesQuery({
    fetchPolicy: "network-only",
  });

  const [createCompanyLiteMutation] = useCreateCompanyLiteMutation();
  const [createCompanyUserToken] =
    useCreateCompanyUserTokenByCompanyIdMutation();

  const currentCompany = data?.companies?.nodes?.[0]?.id;

  const createNewCompany = useCallback(async (): Promise<number | null> => {
    try {
      const { data } = await createCompanyLiteMutation({
        variables: {
          input: {
            translations: [{ lang: Language.En, name: phone }],
          },
        },
        context: { token },
      });

      return data?.createCompanyLite?.success
        ? data.createCompanyLite.company?.id || null
        : null;
    } catch (e) {
      setError("Failed to create company.");
      console.error(e);
      return null;
    }
  }, [createCompanyLiteMutation, phone, token]);

  const createAndSetCompanyUserToken = useCallback(
    async (companyId: number): Promise<void> => {
      try {
        const result = await createCompanyUserToken({
          variables: {
            input: {
              rememberMe: true,
              companyId,
            },
          },
          context: { token },
        });

        const _token =
          result.data?.createCompanyUserTokenByCompanyId?.userToken?.token;

        if (!_token) {
          throw new Error("Token creation failed");
        }

        setToken(_token);
        window.location.href = "/";
      } catch (e) {
        setError("خطا");
        console.error(e);
      }
    },
    [createCompanyUserToken, token]
  );

  useEffect(() => {
    if (loading) {
      return;
    }

    const companyCreation = async () => {
      try {
        const companyId = currentCompany
          ? safeParseInt(currentCompany)
          : await createNewCompany();

        if (!companyId) {
          throw new Error("Company creation failed");
        }

        await createAndSetCompanyUserToken(companyId);
      } catch (e) {
        setError("An unexpected error occurred.");
        console.error(e);
      }
    };

    companyCreation();
  }, [loading, currentCompany, createAndSetCompanyUserToken, createNewCompany]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center h-screen">
        <p className="text-red-500">{error}</p>
      </div>
    );
  }

  return null;
}
